import { getCoordinatesForAddress } from './geocoding';
import { colors } from '../config/colors';
import { resolveCountryCentroid, scoreToRiskLevelKey } from './tenantMapModel';

const getAddressId = (addr) => addr?.addressId ?? addr?.address_id ?? '';
const getRouteStartId = (r) => r?.startPointAddressId ?? r?.start_point_address_id ?? '';
const getRouteEndId = (r) => r?.endPointAddressId ?? r?.end_point_address_id ?? '';
const getRouteId = (r) => r?.routeId ?? r?.route_id ?? null;
const getEndCompanyId = (r) => r?.endPointCompanyId ?? r?.end_point_company_id ?? null;

const formatAddressString = (address) => {
  if (!address) return '';
  if (typeof address.getFullAddress === 'function') return address.getFullAddress();
  return (
    [
      address.streetAddress ?? address.street_address,
      address.city,
      address.stateProvince ?? address.state_province,
      address.postalCode ?? address.postal_code,
      address.country,
    ]
      .filter(Boolean)
      .join(', ') || address.country || ''
  );
};

const priorityColor = (priority) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return colors.danger[500];
  if (p === 'low') return colors.success[500];
  return colors.brand[500];
};

export const markerColorFromRisk = (riskScore, priority) => {
  const score = Number(riskScore);
  if (Number.isFinite(score) && score > 0) {
    if (score >= 7) return colors.danger[500];
    if (score >= 4) return colors.warning[500];
    return colors.success[500];
  }
  return priorityColor(priority);
};

export const routeColorFromRisk = (riskScore) => {
  const score = Number(riskScore);
  if (!Number.isFinite(score) || score <= 0) return colors.brand[500];
  if (score >= 7) return colors.danger[500];
  if (score >= 4) return colors.warning[500];
  return colors.success[500];
};

const normalizeCompanyId = (id) => {
  if (id == null || typeof id === 'boolean') return null;
  const text = String(id).trim();
  if (!text || text === 'true' || text === 'false' || text === 'undefined') return null;
  return text;
};

const extractRouteRiskScore = (analysis) => {
  if (!analysis) return null;
  const score =
    analysis.overallRiskScore ??
    analysis.overall_risk_score ??
    analysis.riskScore ??
    analysis.risk_score;
  return score != null && Number(score) > 0 ? Number(score) : null;
};

/** Flatten DTO / read-model / legacy API shapes for map building. */
export function normalizeWatchlistItemForMap(item) {
  if (!item) return null;

  const watchlistItem = item.watchlistItem ?? item.watchlist_item;
  const companyId =
    item.companyId ??
    item.company_id ??
    watchlistItem?.companyId ??
    watchlistItem?.company_id ??
    '';
  const company = item.company ?? null;
  const priority = item.priority ?? watchlistItem?.priority ?? 'normal';
  const rawInfo = item.userCompanyInfo ?? item.user_company_info;
  const userAddresses =
    rawInfo?.allAddressesForRouteDisplay ??
    rawInfo?.userAddresses ??
    rawInfo?.user_addresses ??
    item.addresses ??
    [];
  const routes = rawInfo?.routes ?? item.routes ?? [];
  const companyType = rawInfo?.companyType ?? rawInfo?.company_type ?? '';

  if (!companyId && !userAddresses.length && !routes.length) {
    return null;
  }

  return {
    ...item,
    companyId,
    company_id: companyId,
    company,
    priority,
    companyType,
    userCompanyInfo: {
      ...(rawInfo && typeof rawInfo === 'object' ? rawInfo : {}),
      userAddresses,
      routes,
    },
  };
}

export function normalizeWatchlistItemsForMap(items = []) {
  return (items || []).map(normalizeWatchlistItemForMap).filter(Boolean);
}

/**
 * Build map markers and routes from watchlist items (sync structure; coords filled async).
 */
export function buildWatchlistMapStructure(watchlistItems = [], riskAnalyses = {}) {
  const routes = [];
  const addressEntries = [];
  const globalAddressMap = new Map();
  const entryByKey = new Map();
  const normalizedItems = normalizeWatchlistItemsForMap(watchlistItems);

  const addressesForItem = (item) =>
    item.userCompanyInfo?.allAddressesForRouteDisplay ??
    item.userCompanyInfo?.userAddresses ??
    item.userCompanyInfo?.user_addresses ??
    [];

  normalizedItems.forEach((item) => {
    const cid = item.companyId ?? item.company_id ?? '';
    const company = item.company || {};
    const companyName = company.name ?? cid;
    const riskScore =
      riskAnalyses[cid]?.overallRiskScore ??
      company.overallRiskScore ??
      company.overall_risk_score;

    addressesForItem(item).forEach((address) => {
      const addressId = getAddressId(address);
      const key = addressId ? String(addressId) : `${cid}-${formatAddressString(address)}`;
      if (!globalAddressMap.has(key)) {
        globalAddressMap.set(key, address);
      }
      if (addressId) {
        globalAddressMap.set(String(addressId), address);
      }

      const existing = entryByKey.get(key);
      if (existing) {
        if (cid && !existing.companyIds.includes(cid)) {
          existing.companyIds.push(cid);
        }
        return;
      }

      const entry = {
        id: `addr-${key}`,
        address,
        addressKey: key,
        companyId: cid,
        companyIds: cid ? [cid] : [],
        companyName,
        priority: item.priority,
        companyType: item.companyType,
        riskScore,
        routeCount: item.userCompanyInfo?.routes?.length ?? 0,
      };
      entryByKey.set(key, entry);
      addressEntries.push(entry);
    });
  });

  normalizedItems.forEach((item) => {
    const cid = item.companyId ?? item.company_id ?? '';
    const routeList = item.userCompanyInfo?.routes ?? [];
    if (!Array.isArray(routeList)) return;

    routeList.forEach((route, idx) => {
      const startId = getRouteStartId(route);
      const endId = getRouteEndId(route);
      const routeId = getRouteId(route);
      routes.push({
        id: `route-${cid}-${routeId ?? idx}`,
        type: 'logistics',
        routeId,
        startId: startId ? String(startId) : null,
        endId: endId ? String(endId) : null,
        fromCompanyId: cid,
        toCompanyId: normalizeCompanyId(getEndCompanyId(route)) ?? cid,
        routeName: route.routeName ?? route.route_name ?? route.name,
        companyName: item.company?.name ?? cid,
        isActive: route.isActive !== false,
      });
    });
  });

  return { addressEntries, routes, globalAddressMap };
}

const resolveCoordsForAddress = async (address) => {
  const coords = await getCoordinatesForAddress(address);
  if (coords && !(coords.lat === 0 && coords.lng === 0)) {
    return coords;
  }
  const country = address?.country;
  const centroid = resolveCountryCentroid(country);
  if (centroid) return centroid;
  return null;
};

export function buildWatchlistNetworkRoutes(markers = [], watchlistItems = []) {
  const markerByCompanyId = new Map();
  markers.forEach((marker) => {
    const ids = marker.companyIds?.length ? marker.companyIds : [marker.companyId];
    ids.forEach((id) => {
      if (id && !markerByCompanyId.has(id)) {
        markerByCompanyId.set(id, marker.position);
      }
    });
  });

  const networkRoutes = [];
  const seen = new Set();
  const normalizedItems = normalizeWatchlistItemsForMap(watchlistItems);

  normalizedItems.forEach((item) => {
    const fromId = normalizeCompanyId(item.companyId ?? item.company_id);
    if (!fromId) return;
    const fromPos = markerByCompanyId.get(fromId);
    if (!fromPos) return;

    (item.userCompanyInfo?.routes ?? []).forEach((route, idx) => {
      const toId = normalizeCompanyId(getEndCompanyId(route)) ?? fromId;
      if (!toId || toId === fromId) return;
      const toPos = markerByCompanyId.get(toId);
      if (!toPos) return;

      const edgeKey = [fromId, toId].sort().join('|');
      if (seen.has(edgeKey)) return;
      seen.add(edgeKey);

      networkRoutes.push({
        id: `network-${fromId}-${toId}-${idx}`,
        type: 'network',
        positions: [fromPos, toPos],
        title: route.routeName ?? route.route_name ?? 'Connection',
        subtitle: item.company?.name ?? fromId,
        fromCompanyId: fromId,
        toCompanyId: toId,
        fromMarkerCompanyId: fromId,
        toMarkerCompanyId: toId,
      });
    });
  });

  return networkRoutes;
}

const isCompanyLikeMarker = (marker) =>
  marker?.type === 'company' || marker?.type === 'supplier';

export function buildHighRiskCompanyMarkers(markers = [], { minScore = 7, limit = 12 } = {}) {
  const companies = markers
    .filter((marker) => isCompanyLikeMarker(marker))
    .map((marker) => ({
      ...marker,
      riskScore: Number(marker.riskScore) > 0 ? Number(marker.riskScore) : 0,
    }))
    .sort((a, b) => b.riskScore - a.riskScore);

  const highRisk = companies.filter((marker) => marker.riskScore >= minScore);
  return (highRisk.length > 0 ? highRisk : companies).slice(0, limit);
}

export async function enrichWatchlistMapData(
  watchlistItems = [],
  { riskAnalyses = {}, routeAnalyses = {} } = {}
) {
  const normalizedItems = normalizeWatchlistItemsForMap(watchlistItems);
  const { addressEntries, routes, globalAddressMap } = buildWatchlistMapStructure(
    normalizedItems,
    riskAnalyses
  );

  const coordByAddressKey = new Map();
  const markers = [];

  const geocodedEntries = await Promise.all(
    addressEntries.map(async (entry) => {
      const coords = await resolveCoordsForAddress(entry.address);
      if (!coords) return null;
      return { entry, coords };
    })
  );

  geocodedEntries.forEach((result) => {
    if (!result) return;
    const { entry, coords } = result;
    coordByAddressKey.set(entry.addressKey, coords);
    const addressId = getAddressId(entry.address);
    if (addressId) {
      coordByAddressKey.set(String(addressId), coords);
    }
    markers.push({
      id: entry.id,
      type: 'company',
      position: [coords.lat, coords.lng],
      title: entry.companyName,
      subtitle: formatAddressString(entry.address),
      iconColor: markerColorFromRisk(entry.riskScore, entry.priority),
      riskScore: entry.riskScore,
      priority: entry.priority,
      companyType: entry.companyType,
      companyId: entry.companyId,
      companyIds: entry.companyIds,
      routeCount: entry.routeCount,
      row: entry.address,
    });
  });

  const resolvedRoutes = [];
  for (const route of routes) {
    const startAddr = route.startId ? globalAddressMap.get(route.startId) : null;
    const endAddr = route.endId ? globalAddressMap.get(route.endId) : null;
    if (!startAddr || !endAddr) continue;

    let startCoords = route.startId ? coordByAddressKey.get(route.startId) : null;
    let endCoords = route.endId ? coordByAddressKey.get(route.endId) : null;

    if (!startCoords) {
      startCoords = await resolveCoordsForAddress(startAddr);
      if (startCoords) coordByAddressKey.set(route.startId, startCoords);
    }
    if (!endCoords) {
      endCoords = await resolveCoordsForAddress(endAddr);
      if (endCoords) coordByAddressKey.set(route.endId, endCoords);
    }

    if (!startCoords || !endCoords) continue;

    const analysis = route.routeId != null ? routeAnalyses[route.routeId] : null;
    const riskScore = extractRouteRiskScore(analysis);

    resolvedRoutes.push({
      id: route.id,
      type: route.type,
      routeId: route.routeId,
      positions: [
        [startCoords.lat, startCoords.lng],
        [endCoords.lat, endCoords.lng],
      ],
      title: route.routeName,
      subtitle: route.companyName,
      fromCompanyId: route.fromCompanyId,
      toCompanyId: route.toCompanyId,
      isActive: route.isActive,
      riskScore,
      riskLevel: riskScore != null ? scoreToRiskLevelKey(riskScore) : 'unknown',
      color: routeColorFromRisk(riskScore),
    });
  }

  const networkRoutes = buildWatchlistNetworkRoutes(markers, normalizedItems);
  const allRoutes = [...resolvedRoutes, ...networkRoutes];

  const bounds = [];
  markers.forEach((m) => bounds.push(m.position));
  allRoutes.forEach((r) => (r.positions || []).forEach((p) => bounds.push(p)));

  return { markers, routes: allRoutes, bounds, logisticsRoutes: resolvedRoutes, networkRoutes };
}
